import React, { useState, useRef } from 'react'
import { Search, Sun, Moon, ChevronDown, Command, X, Menu } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { NotificationBell } from 'components/NotificationBell'
import { CVProcessingIndicator } from 'components/CVProcessingIndicator'
import { AutoSyncButton } from 'features/candidates/components/AutoSyncButton'
import { useAutoSync } from 'contexts/AutoSyncContext'
import { EmailSendingIndicator } from 'components/EmailSendingIndicator'
import { UserDropdown } from 'components/layout/UserDropdown'
import { Dropdown, DropdownItem } from 'components/ui/Dropdown'
import { GlobalSearch } from 'features/search/components/GlobalSearch'
import { useTheme } from 'contexts/ThemeContext'

interface HeaderProps {
  onToggleSidebar?: () => void
}

export function Header({
  onToggleSidebar,
}: HeaderProps) {
  const { theme, toggleTheme } = useTheme()
  const { t, i18n } = useTranslation()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchBarRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const location = useLocation()
  const { isSyncActive } = useAutoSync()
  const showGlobalSearch = location.pathname === '/dashboard'
  
  // Normalize language to 'en' or 'fr', default to 'en'
  const currentLanguage: 'en' | 'fr' = 
    i18n.language === 'fr' || i18n.language.startsWith('fr') ? 'fr' : 'en'

  const handleLanguageChange = (newLang: 'en' | 'fr') => {
    i18n.changeLanguage(newLang)
    console.log('Language changed to:', newLang)
  }

  const handleSearchClick = () => {
    setIsSearchOpen(true)
  }

  const handleSearchClose = () => {
    setIsSearchOpen(false)
    // Keep the search query in the input for user convenience
  }

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (!isSearchOpen) {
      setIsSearchOpen(true)
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  // Handle keyboard shortcut (dashboard only)
  React.useEffect(() => {
    if (!showGlobalSearch) {
      setIsSearchOpen(false)
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsSearchOpen(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showGlobalSearch])

  const getLanguageDisplay = (lang: 'en' | 'fr') => {
    const languages: Record<'en' | 'fr', { name: string; code: string; flag: string; country: string }> = {
      en: { 
        name: 'English', 
        code: 'EN',
        flag: '/flags/en.png',
        country: 'US'
      },
      fr: { 
        name: 'Français', 
        code: 'FR',
        flag: '/flags/fr.png', 
        country: 'FR'
      }
    }
    return languages[lang] || languages.en
  }

  return (
    <header className="sticky top-0 z-50 w-full bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60 rounded-t-2xl">
      <div className="container mx-auto px-4">
        <div className="flex h-14 sm:h-16 items-center justify-between gap-2 sm:gap-3">
          {/* Left Section - Search */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-9 w-9"
              onClick={() => onToggleSidebar?.()}
              aria-label={t('header.openSidebar', 'Open navigation')}
            >
              <Menu className="h-5 w-5" />
            </Button>
            {/* Modern Search Bar */}
            {showGlobalSearch && (
              <div
                ref={searchBarRef}
                className={`relative flex-1 max-w-lg ${isSearchOpen ? 'z-[90]' : ''}`}
              >
                <div className="relative group">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary" />
                  <Input
                    ref={searchInputRef}
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    placeholder={t('header.search')}
                    className="pl-10 pr-10 sm:pr-12 h-9 sm:h-10 bg-muted/50 border-0 focus:bg-background focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-sm"
                    onClick={handleSearchClick}
                    onFocus={handleSearchClick}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {searchQuery && (
                      <button
                        onClick={handleClearSearch}
                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                        title="Clear search"
                      >
                        <X className="h-3 w-3 text-gray-400" />
                      </button>
                    )}
                    <div className="hidden md:flex items-center gap-1">
                    <kbd className="pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 flex">
                      <Command className="h-3 w-3" />K
                    </kbd>
                  </div>
                </div>
                </div>

                {/* Search Modal */}
                {isSearchOpen && (
                  <GlobalSearch
                    isOpen={isSearchOpen}
                    onClose={handleSearchClose}
                    searchBarRef={searchBarRef}
                    searchInputRef={searchInputRef}
                    searchQuery={searchQuery}
                    onSearchQueryChange={setSearchQuery}
                  />
                )}
              </div>
            )}
          </div>

          {/* Right Section - Actions */}
          <div className={`flex items-center gap-2 sm:gap-3 ${isSearchOpen ? 'pointer-events-none' : 'pointer-events-auto'}`}>
            {/* Processing Indicators */}
            {!location.pathname.startsWith('/candidates') && !location.pathname.startsWith('/auto-sync') && isSyncActive && <AutoSyncButton onImportStarted={() => {}} />}
            <CVProcessingIndicator />
            <EmailSendingIndicator />

            {/* Language Selector */}
            <Dropdown
              trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 px-2 sm:px-3 gap-1.5 sm:gap-2 hover:bg-accent transition-all duration-200"
                  >
                    <img 
                      src={getLanguageDisplay(currentLanguage).flag}
                      alt={`${getLanguageDisplay(currentLanguage).name} flag`}
                      className="w-4 h-3 rounded-sm object-cover"
                    />
                    <span className="hidden sm:inline text-xs font-medium">
                      {getLanguageDisplay(currentLanguage).code}
                    </span>
                    <ChevronDown className="hidden sm:block h-3 w-3 text-muted-foreground" />
                  </Button>
              }
            >
              <div className="px-3 py-2 border-b">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('header.language')}
                </p>
              </div>
              <DropdownItem 
                onClick={() => handleLanguageChange('en')}
                className={`${currentLanguage === 'en' ? 'bg-primary/10 text-primary' : ''} transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <img 
                    src="/flags/en.png"
                    alt="US flag"
                    className="w-5 h-4 rounded-sm object-cover"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">English</span>
                    <span className="text-xs text-muted-foreground">United States</span>
                  </div>
                    {currentLanguage === 'en' && (
                      <div className="ml-auto w-2 h-2 bg-primary rounded-full"></div>
                    )}
                </div>
              </DropdownItem>
              
              <DropdownItem
                onClick={() => handleLanguageChange('fr')}
                className={`${currentLanguage === 'fr' ? 'bg-primary/10 text-primary' : ''} transition-colors`}
              >
                <div className="flex items-center gap-3">
                  <img 
                    src="/flags/fr.png"
                    alt="French flag"
                    className="w-5 h-4 rounded-sm object-cover"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">Français</span>
                    <span className="text-xs text-muted-foreground">France</span>
                  </div>
                    {currentLanguage === 'fr' && (
                      <div className="ml-auto w-2 h-2 bg-primary rounded-full"></div>
                    )}
                </div>
              </DropdownItem>
            </Dropdown>

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9 rounded-full hover:bg-accent transition-all duration-200"
              title={t(theme === 'light' ? 'header.switchToDark' : 'header.switchToLight')}
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>

            {/* Notifications */}
            <NotificationBell />
            
            {/* User Section */}
            <UserDropdown />
          </div>
        </div>
      </div>
    </header>
  )
}
